const version = require('../package.json').version

import Vue from 'vue'
import gql from 'graphql-tag'
import PortalVue from 'portal-vue'
import { GraphQLClient } from 'graphql-request'

import _has from 'lodash/has'
import _pick from 'lodash/pick'

import BrowserCookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'
import libDev from './lib-dev'

// API
import Projects from './libs/api/projects'
import FloorPlans from './libs/api/floor_plans'
import Units from './libs/api/units'

import ComponentWrapper from './ComponentWrapper.vue'

Vue.config.productionTip = false

Vue.use(PortalVue)

const DEFAULT_GRAPHQL_CLIENT = 'https://api.edgewise.io/graphql'
const DEFAULT_WEBSOCKET_ENDPOINT = 'wss://api.edgewiserealty.com/socket'

/**
 * Globally register async components for Edgewise Elements.
 *
 * This dynamic import syntax will instruct Webpack to
 * automatically split our built code into bundles which
 * are loaded over async requests when used.
 * See: https://vuejs.org/v2/guide/components-dynamic-async.html
 *
 * Development HTML wrapper located at /public/index.html
 */

Vue.component('Messenger', () => import('./webcomponents/Messenger'))

Vue.component('ProjectContactForm', () =>
  import('./webcomponents/ProjectContactForm')
)
Vue.component('ProjectDocuments', () =>
  import('./webcomponents/ProjectDocuments')
)
Vue.component('ProjectFloorPlansGrid', () =>
  import('./webcomponents/ProjectFloorPlansGrid')
)
Vue.component('ProjectImageGallery', () =>
  import('./webcomponents/ProjectImageGallery')
)
Vue.component('ProjectInventoryGrid', () =>
  import('./webcomponents/ProjectInventoryGrid')
)
Vue.component('ProjectInventoryList', () =>
  import('./webcomponents/ProjectInventoryList')
)
Vue.component('ProjectLotsList', () =>
  import('./webcomponents/ProjectLotsList')
)
Vue.component('ProjectOpenHouseFlash', () =>
  import('./webcomponents/ProjectOpenHouseFlash')
)
Vue.component('UnitBuyNowButton', () =>
  import('./webcomponents/UnitBuyNowButton')
)

/**
 * Edgewise.js SDK
 */
const Edgewise = (opts = {}) => {
  const debug = opts.debug !== undefined ? opts.debug : false

  // opts are assigned to Edgewise.prototype,
  // effectively functioning as global config.
  Edgewise.prototype = Edgewise.prototype
    ? {
        ...Edgewise.prototype,
        ...opts,
        debug,
        version
      }
    : { ...opts, version }

  let _trackerId = null
  let _analyticsOpts = null // eslint-disable-line

  if (debug) {
    console.log(`[edgewise.js] version: v${Edgewise.prototype.version}`)
  }

  setPrototypeFingerprint(opts)
  setPrototypeGraphQLClient(opts)
  setPrototypeWebsocketConnection(opts)

  // Assign the Edgewise prototype to every Vue instance.
  Vue.prototype.$edgewise = Edgewise.prototype

  // ...
  assignUtmSessionData(window.location.search)

  return {
    version,

    /**
     * Edgewise Components
     * Programatic versions of Edgewise WebComponents
     *
     * The `projectId` is required on all components. If not explicitly set
     * on the component, it will be passed in from the components/1 config,
     * then falling back to global config.
     */
    components: (opts = {}) => {
      const componentsProjectId =
        opts.projectId !== undefined
          ? opts.projectId
          : Edgewise.prototype.projectId || null

      const componentsTheme =
        opts.theme !== undefined
          ? opts.theme
          : Edgewise.prototype.theme || 'vanilla'

      // Set the theme's palette css vars
      setThemePaletteVars(componentsTheme)

      // Create the overlay portal (for modals)
      addOverlayPortal(componentsTheme)

      const componentsDebug = opts.debug !== undefined ? opts.debug : debug
      if (componentsDebug) console.log(`[edgewise.js] components init`, opts)

      return {
        create: (type, data) => {
          const mergedData = {
            debug: componentsDebug,
            projectId: componentsProjectId,
            theme: componentsTheme,
            ...opts,
            ...data
          }

          return createEdgewiseComponent(type, mergedData)
        }
      }
    },

    /**
     * Edgewise Analytics
     * NOTE: we do not return a function (a la components) because it would
     * need to be async, and I'm not sure a lot of devs fully understand
     * that concept. At least with `push/3` (which is also async), they don'
     * really need to know what's going on.
     */
    analytics: {
      /**
       * Initialize the tracker by assigning the `tracker_id` to the lobby
       * channel's presence. Can be explicitly overwritten per `push`.
       */
      init: async (trackerId, opts = {}) => {
        _trackerId = trackerId
        _analyticsOpts = opts

        const connection = await Edgewise.prototype.connection
        connection.update({ tracker_id: trackerId })

        if (debug)
          console.log(`[edgewise.js] analytics init: ${trackerId}`, { opts })

        return trackerId
      },

      /**
       */
      push: async (eventName, eventProps = {}, opts = {}) => {
        //eslint-disable-line
        const connection = await Edgewise.prototype.connection

        return _trackerId
          ? (() => {
              if (debug)
                console.log(
                  `[edgewise.js] analytics track`,
                  eventName,
                  eventProps,
                  opts
                )
              const standardProps = buildPageProps(document)
              const props = {
                ...standardProps,
                ...eventProps
              }
              connection.track(eventName, props, opts)
            })()
          : (() => {
              console.error(
                '[edgewise.js] analytics not initialized (missing `trackerId`)'
              )
              return null
            })()
      }
    },

    /**
     * API
     */
    projects: Projects(Vue.prototype.$edgewise),
    floorPlans: FloorPlans(Vue.prototype.$edgewise),
    units: Units(Vue.prototype.$edgewise)
  }
}

/**
 * Exports for all of the edgewise.js library (analytics, components, etc.).
 */
export default Edgewise

/**
 * Create the Edgewise Component prototype.
 * The underlying Vue instance is assigned to `_vue`.
 * We expose Vue's `$mount()` method as `mount()`.
 * We expose the Vue's `$root.$on()` simply as `on()`.
 */
function EdgewiseComponent(vueInstance) {
  this._vue = vueInstance

  this.mount = (selector) => {
    this._vue.$mount(selector)
    return this
  }

  /**
   * In lib, it seems we are only able listen on `this.$root.$emit` events.
   * In WC, it seems $root is ignored, and we must listen on `this.$emit`.
   * So we essentially emit on both (one for lib, and one for wc)
   */
  this.on = (event, handler) => {
    this._vue.$root.$on(event, handler)
    return this
  }

  /**
   * Listen for the internal "ready" event; which is fired when the
   * component is safe to access (this.$root.$emit('ready')).
   * We listen for this event so we can expose certain props / methods to the
   * SDK. Eg, the  Messenger component exposes `openDialog` and `closeDialog()`.
   *
   * Once the those keys have been exposed, we fire a final "mounted" event for
   * the user to consume. We use `ready` internally to not confuse with the Vue
   * lifecycle. The user is unaware that we're using Vue, so they get "mounted".
   *
   * Calling any exposed props / methods before ready will result in
   * a TypeError (eg. "foo is not a function").
   */
  vueInstance.$on('ready', (event) => {
    const componentInstance = event.target

    // Expose the component's props / methods
    componentInstance.$options.expose.forEach((key) => {
      this[key] = componentInstance[key]
    })

    // Fire a final `mounted` event for the user to listen
    // to before calling any exposed props / methods.
    vueInstance.$emit('mounted', event)
  })

  return this
}

/**
 * Creates the overlay portal div.
 * If components() is called in the head, body won't be ready, so we
 * need to first wait for that, then create the portal div.
 */
function addOverlayPortal(theme) {
  if (!document.body) {
    document.addEventListener('DOMContentLoaded', addOverlayPortal, {
      once: true
    })
    return
  }

  if (document.getElementById('ewc-overlay-portal')) {
    return
  }

  const portal = document.createElement('div')
  portal.id = 'ewc-overlay-portal'
  portal.classList.add('ewc', `ewc--theme-${theme}`)
  document.body.appendChild(portal)
}

/**
 * Create an Edgewise Component.
 */
const createEdgewiseComponent = (type, data) => {
  const debug = data.debug !== undefined ? data.debug : false

  if (debug) {
    console.log('[edgewise.js] create component', { type, data, debug })
  }

  const vueInstance = new Vue({
    render: (h) =>
      h(ComponentWrapper, {
        props: {
          elName: type,
          elData: data
        }
      })
  })

  return new EdgewiseComponent(vueInstance)
}

/**
 * First looks for the `edgewise_fingerprint` cookie.
 * If not set, we create a UUID and set the cookie for future use.
 * TODO: consider using fingerprint.js for cross-site tracking.
 */
const getFingerprint = () => {
  const fingerprintCookie = BrowserCookies.get('edgewise_fingerprint') || null

  return fingerprintCookie
    ? fingerprintCookie
    : (() => {
        const uuid = uuidv4().replaceAll('-', '').slice(0, 24)
        BrowserCookies.set('edgewise_fingerprint', uuid, { expires: 1825 })
        return uuid
      })()
}

/**
 * Fingerprint is assigned to the Edgewise prototype.
 * Prevents the need to regenerate the fingerprint on each instance.
 */
const setPrototypeFingerprint = (opts = {}) => {
  Edgewise.prototype.fingerprint =
    Edgewise.prototype.fingerprint || getFingerprint()

  if (opts.debug) {
    console.log(`[edgewise.js] fingerprint: ${Edgewise.prototype.fingerprint}`)
  }

  return Edgewise.prototype
}

/**
 * Create a GraphQL client and assign it to the Edgewise prototype.
 */
const setPrototypeGraphQLClient = (opts = {}) => {
  const graphqlEndpoint = opts.graphqlEndpoint || DEFAULT_GRAPHQL_CLIENT

  Edgewise.prototype.graphqlClient =
    Edgewise.prototype.graphqlClient ||
    new GraphQLClient(graphqlEndpoint, {
      headers: {
        'edgewise-client-name': 'JavaScriptSDK',
        'edgewise-client-id': Edgewise.prototype.version,
        'edgewise-fingerprint': Edgewise.prototype.fingerprint
      }
    })

  if (opts.debug) {
    console.log(`[edgewise.js] graphql endoint: ${graphqlEndpoint}`)
  }

  return Edgewise.prototype
}

/**
 * Connection is assigned to the Edgewise prototype, serving as a
 * socket singleton (all instances of Edgewise share the same connection).
 */
const setPrototypeWebsocketConnection = (opts = {}) => {
  const websocketEndpoint = opts.websocketEndpoint || DEFAULT_WEBSOCKET_ENDPOINT

  Edgewise.prototype.connection =
    Edgewise.prototype.connection ||
    connect(
      {
        uri: websocketEndpoint,
        fingerprint: Edgewise.prototype.fingerprint
      },
      opts
    )

  if (opts.debug) {
    console.log(`[edgewise.js] websocket endoint: ${websocketEndpoint}`)
  }

  return Edgewise.prototype
}

/**
 * Initialize the socket connection, and join the lobby channel.
 * The lobby is used for analytics (tracking) and monitoring chat channels.
 */
const connect = async (params, opts) => {
  const debug = opts.debug !== undefined ? opts.debug : false
  const viewer = await fetchViewer()

  const socketParams = {
    ...params,
    token: viewer.token,
    ipAddress: viewer.ipAddress,
    location: { ...window.location },
    debug
  }

  const { default: socket } = await import('./libs/socket')
  const _socket = socket(socketParams)

  try {
    await _socket.connect()

    if (!_has(_socket, 'channels[lobby].channel')) {
      if (debug) console.log('[edgewise.js] connected.')
      await _socket.join('lobby', { location: { ...window.location } })
    } else {
      if (debug) console.log('[edgewise.js] reconnected.')
    }

    return _socket
  } catch (err) {
    console.error('[edgewise.js] connect error', err)
  }
}

/**
 * Fetch an anonymous user token for
 * use with socket connections.
 */
const fetchViewer = async () => {
  const { viewer } = await Edgewise.prototype.graphqlClient.request(gql`
    query SDKViewer {
      viewer {
        token
        ipAddress
      }
    }
  `)

  return viewer
}

/**
 * Build standard props to sent along with custom properties.
 */
const buildPageProps = (document) => {
  return {
    _location_href: document.location.href,
    _location_origin: document.location.origin,
    _location_host: document.location.host,
    _location_hostname: document.location.hostname,
    _location_pathname: document.location.pathname,
    _referrer: document.referrer,
    _title: document.title
  }
}

/**
 * Capture any UTM parameters and assigns them to session storage
 * for later submission in `ProjectContactForm`.
 */
const assignUtmSessionData = (locationSearch) => {
  const qs = require('qs')

  if (locationSearch) {
    const queryString = locationSearch.slice(1)
    const searchParams = qs.parse(queryString)
    const searchParamKeys = Object.keys(searchParams)
    const utmKeys = searchParamKeys.filter((key) => key.startsWith('utm_'))

    if (utmKeys.length > 0) {
      const utmSessionData = _pick(searchParams, utmKeys)
      sessionStorage.setItem('utmSessionData', JSON.stringify(utmSessionData))
    }
  }
}

/**
 * Assigns theme palette vars to the root in the ewc.theme layer.
 * This allows for conditional palette vars without causing the user to have to
 * increase the specificity (eg, setting palette or design token in .ewc).
 */
const setThemePaletteVars = (theme) => {
  // Prepend the layer order. It's locked once set, so if we set ewc.theme
  // in JS before any CSS is run, we'd inadvertently change te order.

  const order = document.createElement('style')
  order.textContent = `@layer ewc.base, ewc.theme;`
  document.head.prepend(order)

  // Define the possible theme palettes.
  // Design tokens are set via CSS and scoped to the ewc.base css layer;
  // which makes them overridable at :root / html.
  // Note, the "vanilla" theme is defined at the base layer and doesn't have
  // any conditional scope, so it doesn't need JS.

  const ewz = {
    gray200: '#eeeeee',
    gray700: '#616161',
    gray900: '#212121',
    teal600: '#00b0c7'
  }

  const palettes = {
    edgewise: `
      :root {
        --white: #ffffff;
        --light-accent: ${ewz.gray200};
        --accent: ${ewz.teal600};
        --dark-accent: ${ewz.gray700};
        --black: ${ewz.gray900};
      }
    `
  }

  const palette = palettes[theme] || ''
  const style = document.createElement('style')

  style.textContent = `
    @layer ewc.base, ewc.theme;

    @layer ewc.theme {
      ${palette}
    }
  `

  document.head.appendChild(style)
}

/**
 * Dev only, instantiate one / many components.
 */
if (process.env.NODE_ENV === 'development') {
  libDev(Edgewise)
}
